import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import '../../styles/mcq-nav.css';
import '../../styles/prepare-up-home.css';
import '../../styles/prepare-up-flashcards.css';
import '../../styles/prepare-up-mcq.css';
import { renderMarkdown } from './markdown';
import { IconCheck, IconX, IconArrowRight, IconArrowLeft, IconClock, IconInfo } from './icons';
import LearnPanel, { useLearnOpen } from './LearnPanel';
import QuestionList, { useQuestionList } from './QuestionList';
import { buildPaths, getHubTreeCached } from './paths';
import { Bar, HomeSearch, Loading, ThemeToggle, buildSearchEntries } from './HomeDashboard';
import { logMcqAnswered, conceptsFromText } from './memoryLog';
import APIService from '../../services/APIService';
import * as sessionCache from './sessionCache';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const LETTERS = ['A', 'B', 'C', 'D'];

const buildPrompt = (topics, difficulty, recent) =>
  `You are an expert quiz author. Generate ONE multiple-choice question on these topics: ${topics.join(', ')}.` +
  ` Difficulty: ${difficulty}.\n` +
  `Return ONLY a compact JSON object — no markdown fences, no extra prose — with exactly these keys:\n` +
  `{"question":"...","options":{"A":"...","B":"...","C":"...","D":"..."},"answer":"A","explanation":"..."}\n` +
  `"answer" must be one of A, B, C, D. Make it a NEW question, different from: ${recent.join(' | ') || 'none'}.`;

// Fetch one MCQ as JSON by streaming /chat and parsing the assembled text.
async function fetchMcq(topics, difficulty, recent, signal) {
  const q = buildPrompt(topics, difficulty, recent);
  const resp = await fetch(`${API_BASE_URL}/chat?q=${encodeURIComponent(q)}`, { signal });
  if (!resp.ok || !resp.body) throw new Error('Could not reach the AI. Is the backend running?');
  const reader = resp.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  let full = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const lines = buf.split('\n');
    buf = lines.pop();
    for (const line of lines) {
      if (!line.startsWith('data:')) continue;
      const p = line.slice(5).trim();
      if (!p || p === '[DONE]') continue;
      let t; try { t = JSON.parse(p); } catch { t = p; }
      full += t;
    }
  }
  const s = full.indexOf('{');
  const e = full.lastIndexOf('}');
  if (s === -1 || e === -1) throw new Error('The AI returned an unexpected format. Try again.');
  const obj = JSON.parse(full.slice(s, e + 1));
  if (!obj.question || !obj.options || !obj.answer) throw new Error('Incomplete question. Try again.');
  return obj;
}

/*
 * McqQuiz — a topic-scoped MCQ session. Questions come one at a time from the
 * session cache / engine (or the LLM when there's no concept scope), are graded
 * client-side with an explanation, and every answer is logged to memory.
 *
 * The session keeps the questions already loaded (with your answers) so you can
 * go back with Previous or jump from the Questions list; "Next Question" moves
 * forward or fetches the next one. Its length is the session size the learner
 * picked (e.g. "10 MCQs"); at the end a summary offers to keep going.
 */
const BOOKMARK_KEY = 'pu_mcq_bookmarks_v1';
const REVIEW_KEY = 'pu_mcq_review_v1';
const loadSet = (k) => { try { return new Set(JSON.parse(localStorage.getItem(k)) || []); } catch { return new Set(); } };
const saveSet = (k, set) => { try { localStorage.setItem(k, JSON.stringify([...set])); } catch { /* private mode */ } };
const qKey = (q) => String(q.item_id || q.question || '');
const plain = (t) => String(t || '').replace(/[*_`#>]/g, '').replace(/\s+/g, ' ').trim();

const IconBookmark = ({ size = 18, filled = false }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
  </svg>
);

const McqQuiz = ({
  topics, difficulty = 'Mixed', conceptIds = [], engineDifficulty = null,
  sessionSize = 10, onBack, profile, onEditProfile, theme, onToggleTheme, onOpenPath, onOpenHubNode,
}) => {
  const [items, setItems] = useState([]);          // [{ q, selected }] — this session's questions
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [target, setTarget] = useState(Math.max(1, sessionSize || 10));
  const [finished, setFinished] = useState(false);
  const [bookmarks, setBookmarks] = useState(() => loadSet(BOOKMARK_KEY));
  const [review, setReview] = useState(() => loadSet(REVIEW_KEY));
  const [listOpen, setListOpen] = useQuestionList();
  const [hub, setHub] = useState(null);
  const [learnOpen, toggleLearn] = useLearnOpen('mcq');
  const recentRef = useRef([]);
  const itemsRef = useRef(items); itemsRef.current = items;
  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    let live = true;
    getHubTreeCached().then((d) => { if (live) setHub(d.tree || []); }).catch(() => { if (live) setHub([]); });
    return () => { live = false; };
  }, []);
  const searchEntries = useMemo(() => {
    const p = hub ? buildPaths(hub) : null;
    return buildSearchEntries(hub, p ? [...p.roles, ...p.areas] : []);
  }, [hub]);

  // Parents rebuild the topics/conceptIds ARRAYS every render — depending on
  // them directly made `load` change identity and refetch a fresh question on
  // any parent re-render (the refresh double-load). Depend on VALUE keys and
  // read the live arrays through refs instead.
  const topicsRef = useRef(topics); topicsRef.current = topics;
  const cidsRef = useRef(conceptIds); cidsRef.current = conceptIds;
  const scopeKey = `${conceptIds.join('|')}::${topics.join('|')}::${difficulty}::${engineDifficulty || ''}`;

  // Fetch ONE new question and append it to the session.
  const load = useCallback(async () => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    setLoading(true); setErr('');
    try {
      // Config-driven: take a pre-generated MCQ from the cache (instant); fall back
      // to a live engine call, and keep the queue warm in the background.
      const conceptIds = cidsRef.current;
      const topics = topicsRef.current;
      let mcq;
      if (conceptIds.length) {
        mcq = sessionCache.take(conceptIds, 'mcq');
        if (!mcq) mcq = await APIService.engineMcq({ conceptIds, recent: recentRef.current.slice(-5), difficulty: engineDifficulty });
        sessionCache.refill(conceptIds, 'mcq', 2);
      } else {
        mcq = await fetchMcq(topics, difficulty, recentRef.current.slice(-5), abortRef.current.signal);
      }
      if (!mountedRef.current) return;
      recentRef.current.push(mcq.question);
      const at = itemsRef.current.length;
      setItems((list) => [...list, { q: mcq, selected: null }]);
      setIdx(at);
    } catch (e) {
      if (e.name !== 'AbortError' && mountedRef.current) setErr(e.message || 'Could not load a question.');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  useEffect(() => {
    mountedRef.current = true;
    setItems([]); setIdx(0); setFinished(false);
    load();
    return () => { mountedRef.current = false; abortRef.current?.abort(); };
  }, [load]);

  const cur = items[idx];
  const q = cur?.q;
  const selected = cur?.selected || null;

  const choose = (letter) => {
    if (!q || selected) return;
    setItems((list) => list.map((it, n) => (n === idx ? { ...it, selected: letter } : it)));
    const correct = letter === q.answer;
    const cids = (q.concept_ids && q.concept_ids.length)
      ? q.concept_ids
      : (conceptIds.length ? conceptIds : conceptsFromText('mcq', topics));
    logMcqAnswered(cids, correct, {
      question: q.question, selected: letter, answer: q.answer,
      item_id: q.item_id || null, depth: q.depth || null,
    });
  };

  const answered = items.filter((it) => it.selected).length;
  const correctCount = items.filter((it) => it.selected && it.selected === it.q.answer).length;
  const atEnd = idx >= items.length - 1;
  const canNext = !loading && (!atEnd || !!selected);

  const next = useCallback(() => {
    if (loading) return;
    if (idx < items.length - 1) { setIdx(idx + 1); return; }
    if (!items[idx]?.selected) return;
    if (items.length >= target) { setFinished(true); return; }
    load();
  }, [loading, idx, items, target, load]);
  const prev = useCallback(() => { if (idx > 0) { setFinished(false); setIdx(idx - 1); } }, [idx]);
  const pick = useCallback((n) => { setFinished(false); setIdx(n); }, []);
  const keepGoing = () => { setFinished(false); setTarget((t) => t + Math.max(1, sessionSize || 10)); load(); };

  const toggleIn = (setter, storageKey, key) => setter((prevSet) => {
    const nextSet = new Set(prevSet);
    if (nextSet.has(key)) nextSet.delete(key); else nextSet.add(key);
    saveSet(storageKey, nextSet);
    return nextSet;
  });

  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (!q || loading || finished) return;
      const k = e.key.toUpperCase();
      if (!selected && ['A', 'B', 'C', 'D'].includes(k)) choose(k);
      else if (!selected && ['1', '2', '3', '4'].includes(e.key)) choose(LETTERS[+e.key - 1]);
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight' && canNext) next();
      else if (selected && (k === 'N' || (e.key === 'Enter' && t?.tagName !== 'BUTTON'))) next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, selected, loading, finished, next, prev, canNext]);

  const title = topics.slice(0, 2).join(' · ') + (topics.length > 2 ? ` +${topics.length - 2}` : '');
  const displayName = profile?.display_name || profile?.name || '';
  const minutes = Math.max(1, Math.round(Math.max(0, target - answered) * 1.2));   // ~70s a question
  const key = q ? qKey(q) : '';
  const shown = Math.min(idx + 1, target);

  return (
    <div className="pu-home pu-flash pu-mcq">
      <div className="pu-page">
        <header className="pu-topbar">
          <HomeSearch entries={searchEntries}
            onPick={(e) => (e.kind === 'track' ? onOpenPath?.(e.id) : onOpenHubNode?.(e.id))} />
          <div className="pu-topbar-end">
            {onToggleTheme && <ThemeToggle theme={theme} onToggle={onToggleTheme} />}
            <button type="button" className="pu-avatar" onClick={onEditProfile}
              aria-label={displayName ? `${displayName} — profile` : 'Profile'}>
              {profile?.avatar ? <img src={profile.avatar} alt="" /> : <span>{(displayName || 'U').slice(0, 1).toUpperCase()}</span>}
            </button>
          </div>
        </header>

        <div className="learn-split fx-split">
          {learnOpen && (
            <LearnPanel conceptId={(q && q.concept_ids && q.concept_ids[0]) || conceptIds[0]} onClose={toggleLearn} />
          )}
          <div className="learn-main">
            {/* Header — compact; the topic lives here */}
            <section className="fx-head" aria-labelledby="mq-title">
              {onBack && (
                <button type="button" className="pu-link fx-backlink" onClick={onBack}>
                  <IconArrowLeft size={14} /> Back to Practice
                </button>
              )}
              <p className="pu-kicker">MCQ</p>
              <h1 id="mq-title">{title || 'MCQ practice'}</h1>
              <p className="fx-lede">Test your understanding with practice questions.</p>
              <div className="fx-meta">
                <span className="fx-pill"><IconClock size={14} /> About {minutes} min left</span>
                <button type="button" className={`pu-link fx-notes${learnOpen ? ' on' : ''}`} onClick={toggleLearn} aria-pressed={learnOpen}>
                  {learnOpen ? 'Hide concept notes' : 'Concept notes'}
                </button>
              </div>
            </section>

            <div className={`fx-body${listOpen ? ' list-open' : ''}`}>
              <section className="pu-panel fx-work mq-work" aria-label="Question">
                {finished ? (
                  <div className="mq-done" role="status">
                    <p className="pu-kicker">Session complete</p>
                    <h2>{correctCount} of {answered} correct</h2>
                    <div className="pu-goal-bar"><Bar value={answered ? correctCount / answered : 0} label={`${correctCount} of ${answered} correct`} /><b>{answered ? Math.round((correctCount / answered) * 100) : 0}%</b></div>
                    <p className="pu-meta">Your answers are saved to your memory — weak spots will come back in future sessions.</p>
                    <div className="mq-done-actions">
                      <button type="button" className="pu-btn pu-btn-primary" onClick={keepGoing}>Keep going <IconArrowRight size={15} /></button>
                      {onBack && <button type="button" className="pu-btn pu-btn-quiet" onClick={onBack}>Back to Practice</button>}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="fx-toolbar">
                      <span className="fx-count">Question {shown} of {target}</span>
                      <span className="fx-count-bar"><Bar value={shown / target} label={`Question ${shown} of ${target}`} /></span>
                      {q && (
                        <>
                          <label className="fx-check">
                            <input type="checkbox" checked={review.has(key)} onChange={() => toggleIn(setReview, REVIEW_KEY, key)} />
                            Mark for review
                          </label>
                          <button type="button" className={`fx-icon-btn${bookmarks.has(key) ? ' on' : ''}`}
                            onClick={() => toggleIn(setBookmarks, BOOKMARK_KEY, key)}
                            aria-pressed={bookmarks.has(key)} aria-label={bookmarks.has(key) ? 'Remove bookmark' : 'Bookmark this question'}
                            title={bookmarks.has(key) ? 'Bookmarked' : 'Bookmark'}>
                            <IconBookmark filled={bookmarks.has(key)} />
                          </button>
                        </>
                      )}
                    </div>

                    {loading && atEnd && !selected ? (
                      <div className="mq-loading" aria-live="polite"><p className="pu-meta">Preparing your next question…</p><Loading rows={5} /></div>
                    ) : err && !q ? (
                      <div className="mq-err" role="alert">
                        <p>{err}</p>
                        <button type="button" className="pu-btn pu-btn-quiet" onClick={load}>Try again</button>
                      </div>
                    ) : q ? (
                      <div className="mq-q-wrap" key={`${idx}-${qKey(q)}`}>
                        {(q.tag || q.why) && (
                          <p className="mq-pick">
                            {q.tag && <span className="fx-tag">{q.tag}</span>}
                            {q.why && <span>{q.why}</span>}
                          </p>
                        )}
                        <div className="mq-q">{renderMarkdown(q.question)}</div>
                        <div className="mq-options" role="group" aria-label="Answer options">
                          {LETTERS.map((L) => {
                            const isCorrect = selected && L === q.answer;
                            const isWrongPick = selected && L === selected && L !== q.answer;
                            return (
                              <button key={L} type="button" disabled={!!selected} onClick={() => choose(L)}
                                className={`mq-opt${isCorrect ? ' correct' : ''}${isWrongPick ? ' wrong' : ''}${selected ? ' revealed' : ''}`}
                                aria-label={`${L}: ${plain(q.options[L])}${isCorrect ? ' — correct answer' : ''}${isWrongPick ? ' — your answer, incorrect' : ''}`}>
                                <span className="mq-letter">{L}</span>
                                <span className="mq-text">{q.options[L]}</span>
                                {isCorrect && <IconCheck size={18} />}
                                {isWrongPick && <IconX size={18} />}
                              </button>
                            );
                          })}
                        </div>

                        {selected && (
                          <div className="mq-explain">
                            <span className="mq-explain-icon" aria-hidden="true"><IconInfo size={18} /></span>
                            <div>
                              <p className={`mq-verdict ${selected === q.answer ? 'ok' : 'no'}`} role="status">
                                {selected === q.answer ? 'Correct!' : `Not quite — the answer is ${q.answer}.`}
                              </p>
                              <h3>Explanation</h3>
                              {q.explanation ? <div className="mq-explain-body">{renderMarkdown(q.explanation)}</div>
                                : <p className="mq-explain-body">The correct answer is {q.answer}: {plain(q.options[q.answer])}.</p>}
                            </div>
                          </div>
                        )}
                        {err && <p className="mq-inline-err" role="alert">{err} <button type="button" className="pu-link" onClick={load}>Try again</button></p>}
                      </div>
                    ) : null}

                    <div className="mq-actions">
                      <button type="button" className="pu-btn fx-next mq-prev" onClick={prev} disabled={idx === 0}>
                        <IconArrowLeft size={15} /> Previous
                      </button>
                      <button type="button" className="pu-btn pu-btn-primary" onClick={next} disabled={!canNext}>
                        {atEnd && items.length >= target && selected ? 'Finish' : 'Next Question'} <IconArrowRight size={15} />
                      </button>
                    </div>
                    <p className="fx-keys" aria-hidden="true"><kbd>A</kbd>–<kbd>D</kbd> answer · ← / → move · <kbd>N</kbd> next</p>
                  </>
                )}
              </section>

              <QuestionList
                open={listOpen}
                setOpen={setListOpen}
                total={target}
                done={answered}
                current={finished ? -1 : idx}
                pending={Math.max(0, target - items.length)}
                onPick={pick}
                label="Question"
                items={items.map((it, n) => ({
                  key: `${n}-${qKey(it.q)}`,
                  title: plain(it.q.question),
                  state: it.selected ? (it.selected === it.q.answer ? 'got' : 'again') : null,
                  sub: `${it.q.tag || 'Question'}${it.selected ? (it.selected === it.q.answer ? ' · Correct' : ' · Incorrect') : ''}${review.has(qKey(it.q)) ? ' · Marked' : ''}`,
                }))}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default McqQuiz;
